import { useEffect, useRef } from 'react';
import { ToastAction } from '@/components/ui/Toast';
import { toast } from '@/hooks/useToast';
import { useBoard } from './hooks';
import { drName } from './DoctorAlertPopup';

/**
 * Tells ED staff the moment a paged doctor answers "can't come" (or does not
 * answer at all): a toast that stays until dismissed, naming the doctor, the
 * reason and the patient, with "Open" to go straight to the encounter and
 * choose another doctor.
 *
 * Runs on the board's own query (same key as TrackingBoard, so no extra
 * request), which keeps polling while a workspace is open. Pages already
 * declined when the screen first loads are not announced — the board and the
 * encounter banner already show them.
 */
export function useDeclineNotifier(onOpen: (encounterId: number) => void, enabled: boolean) {
  const board = useBoard();
  const seen = useRef<Set<number> | null>(null);

  useEffect(() => {
    if (!enabled || !board.data) return;
    const answered = board.data.rows.filter(
      (r) => r.alert_id && (r.alert_status === 'declined' || r.alert_status === 'expired'),
    );
    if (seen.current === null) {
      seen.current = new Set(answered.map((r) => r.alert_id as number));
      return;
    }
    for (const r of answered) {
      const id = r.alert_id as number;
      if (seen.current.has(id)) continue;
      seen.current.add(id);
      const declined = r.alert_status === 'declined';
      toast({
        title: declined
          ? `${drName(r.attending_doctor_name)} can't come — ${r.alert_decline_reason ?? 'no reason given'}`
          : `${drName(r.attending_doctor_name)} has not answered the page`,
        description: `${r.patient_name} (${r.er_number}): choose another doctor.`,
        variant: 'danger',
        duration: Infinity,
        action: <ToastAction altText="Open the patient" onClick={() => onOpen(r.id)}>Open</ToastAction>,
      });
    }
  }, [board.data, enabled, onOpen]);
}
